import { Package, PackageSchema } from '@features/DynamicPackagesConfigurationWizard/types';
import { PriceSchema } from '@features/state';
import { z } from 'zod';

import { OptionalPaymentTermsSchema, OptionalRawPaymentTermsSchema } from './PaymentTerms.schema';

export const RoomUpgradeSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string(),
  image_url: z.string().nullable(),
  price_per_night: PriceSchema,
  price_per_stay: PriceSchema,
  price_difference: PriceSchema,
  beds: z
    .object({
      total: z.number(),
      types: z
        .array(z.object({ name: z.string(), quantity: z.number() }))
        .nullable()
        .optional(),
    })
    .optional()
    .nullable(),
  amenities: z.array(z.string()).optional().nullable(),
  max_occupancy: z.number(),
  max_adults: z.number().nullable(),
  max_children: z.number().nullable(),
  quantity: z.number().optional().nullable(),
});

const ParsedPriceSchema = z.object({
  amount: z.number(),
  currencyCode: z.string(),
  prefix: z.string().optional(),
  suffix: z.string().optional(),
});

export const ParsedRoomUpgradeSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string(),
  imageUrl: z.string().nullable(),
  pricePerNight: ParsedPriceSchema,
  pricePerStay: ParsedPriceSchema,
  priceDifference: ParsedPriceSchema,
  beds: z
    .object({
      total: z.number(),
      types: z
        .array(z.object({ name: z.string(), quantity: z.number() }))
        .nullable()
        .optional(),
    })
    .optional(),
  amenities: z.array(z.string()).optional().nullable(),
  maxOccupancy: z.number(),
  maxAdults: z.number().nullable(),
  maxChildren: z.number().nullable(),
  minAdults: z.number().nullable(),
  minChildren: z.number().nullable(),
  quantity: z.number().nullable().optional(),
});

export const RateRoomSchema = z.object({
  description: z.string(),
  displayUrgencyMessaging: z.boolean().nullish(),
  id: z.string(),
  imageUrl: z.string().nullable(),
  maxAdults: z.number().nullable(),
  maxChildren: z.number().nullish(),
  maxOccupancy: z.number().nullish(),
  minAdults: z.number().nullish(),
  minChildren: z.number().nullish(),
  name: z.string(),
  pricePerNight: ParsedPriceSchema,
  pricePerStay: ParsedPriceSchema,
  quantity: z.number().nullable(),
  roomUpgrade: ParsedRoomUpgradeSchema.optional().nullable(),
  type: z.string().optional(),
  amenities: z.array(z.string()).nullable().optional(),
  priceDifference: ParsedPriceSchema.optional().nullable(),
  priority: z.number().nullish(),
});

export const StaticRateSchema = z.object({
  _id: z.string().optional(),
  _type: z.literal('Rate'),
  rateCode: z.string(),
  name: z.string().nullable(),
  description: z.string().nullable(),
  images: z.array(z.string()).nullable(),
  cancellationPolicyText: z.string().nullable(),
  priority: z.number().nullish(),
  type: z.enum(['Standard', 'Promo']).optional().catch(undefined),
  visibility: z.enum(['Public', 'Private']).optional().catch(undefined),
});

const RawPackageExperienceOptionSchema = z.object({
  item_id: z.string(),
  item_type: z.string(),
  name: z.string(),
  meta: z.object({
    entity_id: z.string(),
    entity_type: z.literal('SpaPackageConfigurationGroupOption'),
  }),
});

const RawPackageExperienceSchema = z.object({
  number_of_selections_required: z.number(),
  id: z.string(),
  options: z.array(RawPackageExperienceOptionSchema),
  delay_after: z.number().optional(),
  day_number: z.number(),
  day_number_range: z.array(z.number()).optional(),
  meta: z.object({
    entity_id: z.string(),
    entity_type: z.literal('SpaPackageConfigurationGroup'),
  }),
});

const RawPackageRestaurantSchema = z.object({
  id: z.string().optional(),
  restaurant_id: z.string(),
  menu_ids: z.array(z.string()),
  delay_after: z.number().optional(),
  name: z.string().optional().nullable(),
  time_restrictions: z.object({ start_time: z.string(), end_time: z.string() }).nullable(),
  day_number: z.number(),
  day_number_range: z.array(z.number()),
});

const RawPackageSchema = z.object({
  property_id: z.string(),
  rate_id: z.string(),
  number_of_days: z.number(),
  experiences: z.array(RawPackageExperienceSchema),
  restaurants: z.array(RawPackageRestaurantSchema).optional(),
  meta: z
    .object({
      entity_type: z.literal('SpaPackage').optional(),
      entity_id: z.string().optional(),
    })
    .optional(),
});

export const AvailableRateSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  image_url: z.string().nullable(),
  from_price: PriceSchema.nullable(),
  package: RawPackageSchema.nullable().optional(),
  payment_terms: OptionalRawPaymentTermsSchema,
});

export const RoomRateSchema = z
  .object({
    rates: z.array(
      AvailableRateSchema.extend({
        from_price: z.object({
          amount: z.number(),
          currency_code: z.string(),
        }),
        terms: z.string().nullable(),
        room_upgrade: RoomUpgradeSchema.nullable().optional(),
        quantity: z.number(),
      })
    ),
  })
  .nullable();

export const SingleAvailableRateSchema = z
  .object({
    id: z.string().nullable(),
    name: z.string().nullable(),
    rooms: z.array(RateRoomSchema),
    terms: z.string().nullable(),
    description: z.string().nullable(),
    imageUrl: z.string().nullable(),
    fromPrice: ParsedPriceSchema.nullable(),
    package: PackageSchema.nullable().optional(),
  })
  .nullable();

export const RateSchema = z.object({
  id: z.string(),
  uuid: z.string().optional(),
  title: z.string(),
  description: z.string(),
  imageUrl: z.string(),
  images: z.array(z.string()).optional(),
  cancellationPolicyText: z.string().nullable(),
  price: z
    .object({
      amount: z.number(),
      currencyCode: z.string(),
      prefix: z.string(),
      suffix: z.string(),
    })
    .optional()
    .nullable(),
  priority: z.number().nullish(),
  roomUpgrade: ParsedRoomUpgradeSchema.nullable().optional(),
  quantity: z.number().optional(),
  rooms: z.array(RateRoomSchema).optional(),
  // @TODO: fix unnecessary camel case and double-ngative. Own issue as wider impact.
  unAvailable: z.boolean().optional(),
  package: z.custom<Package>().nullable().optional(),
  paymentTerms: OptionalPaymentTermsSchema,
});
